import { useCallback, useEffect, useId, useState, type FormEvent } from 'react';

import { ApiError, usersApi, type CallerView } from '../api/client';
import {
  Alert,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  PageLayout,
  PasswordInput,
  RowActions,
  Section,
  StatusChip,
  Table,
} from '../components';
import { useI18n } from '../i18n';
import { useSession } from '../auth/session-context';

/**
 * Callerverwaltung für Kundenadmins.
 *
 * Anlegen, Passwort neu setzen, Administratorrechte geben oder entziehen,
 * Zugang sperren. Mehr Rechte gibt es bewusst nicht
 * (01_roles_and_accounts.md).
 *
 * Alle folgenschweren Schritte laufen über eigene Dialoge - `window.prompt`
 * und `window.confirm` kommen hier nicht mehr vor: sie lassen sich nicht
 * übersetzen, sehen in jedem Browser anders aus und ein Passwort gehört
 * ohnehin in ein maskiertes Feld.
 */

/** Welcher Dialog gerade offen ist. */
type Dialog =
  | { art: 'passwort'; caller: CallerView }
  | { art: 'sperren'; caller: CallerView }
  | { art: 'adminGeben'; caller: CallerView }
  | { art: 'adminEntziehen'; caller: CallerView }
  | null;

export function CallersPage(): JSX.Element {
  const { t, tError } = useI18n();
  const { principal, handleApiError } = useSession();
  const nameId = useId();
  const passwortId = useId();

  const [callers, setCallers] = useState<CallerView[]>([]);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [neuesPasswort, setNeuesPasswort] = useState('');
  const [dialog, setDialog] = useState<Dialog>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialogFehler, setDialogFehler] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = useCallback(
    async (action: () => Promise<void>, imDialog = false): Promise<boolean> => {
      setError(null);
      setDialogFehler(null);
      setBusy(true);
      try {
        await action();
        return true;
      } catch (caught) {
        handleApiError(caught);
        const text = caught instanceof ApiError ? tError(caught.code) : tError(undefined);
        if (imDialog) {
          setDialogFehler(text);
        } else {
          setError(text);
        }
        return false;
      } finally {
        setBusy(false);
      }
    },
    [handleApiError, tError],
  );

  const reload = useCallback(async () => {
    const response = await usersApi.listCallers();
    setCallers(response.callers);
  }, []);

  useEffect(() => {
    void run(reload);
  }, [reload, run]);

  const anlegen = (event: FormEvent): void => {
    event.preventDefault();
    void run(async () => {
      await usersApi.createCaller(username, password);
      setUsername('');
      setPassword('');
      setMessage(t('callers.created'));
      await reload();
    });
  };

  const schliessen = (): void => {
    setDialog(null);
    setNeuesPasswort('');
    setDialogFehler(null);
  };

  const bestaetigen = (): void => {
    if (!dialog) {
      return;
    }
    const { art, caller } = dialog;

    void (async () => {
      const erfolg = await run(async () => {
        if (art === 'passwort') {
          await usersApi.setPassword(caller.id, neuesPasswort);
          setMessage(t('callers.newPasswordSaved'));
          return;
        }
        if (art === 'sperren') {
          await usersApi.setActive(caller.id, false);
        } else {
          await usersApi.setAdminRights(caller.id, art === 'adminGeben');
        }
        await reload();
      }, true);

      if (erfolg) {
        schliessen();
      }
    })();
  };

  const eigeneId = principal?.kind === 'tenant_user' ? principal.id : null;

  return (
    <PageLayout>
      <PageHeader title={t('callers.title')} description={t('callers.intro')} />

      {error ? <Alert tone="error">{error}</Alert> : null}
      {message ? <Alert tone="success">{message}</Alert> : null}

      <Card>
        <Section title={t('callers.createTitle')}>
          <form onSubmit={anlegen} className="form form-inline">
            <FormField label={t('callers.username')} htmlFor={nameId} help={t('callers.help.username')}>
              <Input id={nameId} value={username} onChange={setUsername} autoComplete="off" />
            </FormField>
            <FormField label={t('callers.password')} htmlFor={passwortId}>
              <PasswordInput
                id={passwortId}
                value={password}
                onChange={setPassword}
                autoComplete="new-password"
              />
            </FormField>
            <Button type="submit" variant="primary" disabled={busy}>
              {t('callers.add')}
            </Button>
          </form>
        </Section>
      </Card>

      <Card>
        {callers.length === 0 ? (
          <EmptyState title={t('callers.empty')} description={t('callers.intro')} />
        ) : (
          <Table
            label={t('callers.title')}
            headers={[t('callers.username'), t('callers.role'), t('callers.state'), '']}
          >
            {callers.map((caller) => (
              <tr key={caller.id}>
                <td>
                  <strong>{caller.username ?? caller.email}</strong>
                  {caller.id === eigeneId ? ' · ' + t('callers.you') : null}
                </td>
                <td>
                  <StatusChip tone={caller.isAdmin ? 'info' : 'neutral'}>
                    {caller.isOwner
                      ? t('callers.roleOwner')
                      : caller.isAdmin
                        ? t('callers.roleAdmin')
                        : t('callers.roleCaller')}
                  </StatusChip>
                </td>
                <td>
                  <StatusChip tone={caller.isActive ? 'erfolg' : 'gefahr'}>
                    {caller.isActive ? t('callers.stateActive') : t('callers.stateInactive')}
                  </StatusChip>
                </td>
                <RowActions>
                  <Button variant="ghost" disabled={busy} onClick={() => setDialog({ art: 'passwort', caller })}>
                    {t('callers.newPassword')}
                  </Button>

                  {caller.isOwner ? null : (
                    <>
                      <Button
                        variant="ghost"
                        disabled={busy}
                        onClick={() =>
                          caller.isAdmin
                            ? setDialog({ art: 'adminEntziehen', caller })
                            : setDialog({ art: 'adminGeben', caller })
                        }
                      >
                        {caller.isAdmin ? t('callers.revokeAdmin') : t('callers.makeAdmin')}
                      </Button>

                      {caller.isActive ? (
                        <Button variant="ghost" disabled={busy} onClick={() => setDialog({ art: 'sperren', caller })}>
                          {t('callers.deactivate')}
                        </Button>
                      ) : (
                        // Das Freigeben ist harmlos und braucht keine Rückfrage.
                        <Button
                          variant="ghost"
                          disabled={busy}
                          onClick={() =>
                            void run(async () => {
                              await usersApi.setActive(caller.id, true);
                              await reload();
                            })
                          }
                        >
                          {t('callers.activate')}
                        </Button>
                      )}
                    </>
                  )}
                </RowActions>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {dialog?.art === 'passwort' ? (
        <ConfirmDialog
          title={t('callers.setPasswordTitle')}
          message={t('callers.setPasswordText')}
          confirmLabel={t('callers.setPasswordAction')}
          tone="primary"
          busy={busy}
          error={dialogFehler}
          onCancel={schliessen}
          onConfirm={bestaetigen}
        >
          <FormField label={t('callers.newPassword')}>
            <PasswordInput value={neuesPasswort} onChange={setNeuesPasswort} autoComplete="new-password" />
          </FormField>
        </ConfirmDialog>
      ) : null}

      {dialog?.art === 'sperren' ? (
        <ConfirmDialog
          title={t('callers.deactivateTitle')}
          message={t('callers.deactivateText')}
          confirmLabel={t('callers.deactivateAction')}
          busy={busy}
          error={dialogFehler}
          onCancel={schliessen}
          onConfirm={bestaetigen}
        />
      ) : null}

      {dialog?.art === 'adminGeben' ? (
        <ConfirmDialog
          title={t('callers.makeAdminTitle')}
          message={t('callers.makeAdminText')}
          confirmLabel={t('callers.makeAdmin')}
          tone="primary"
          busy={busy}
          error={dialogFehler}
          onCancel={schliessen}
          onConfirm={bestaetigen}
        />
      ) : null}

      {dialog?.art === 'adminEntziehen' ? (
        <ConfirmDialog
          title={t('callers.revokeAdminTitle')}
          message={t('callers.revokeAdminText')}
          confirmLabel={t('callers.revokeAdmin')}
          busy={busy}
          error={dialogFehler}
          onCancel={schliessen}
          onConfirm={bestaetigen}
        />
      ) : null}
    </PageLayout>
  );
}
